<template>
    <div class="timeline-shell">
        <div ref="scrollContainer" class="timeline-scroll" @scroll="handleScroll">
            <div
                class="timeline-canvas"
                :style="{ '--timeline-columns': days.length }"
            >
                <div ref="timelineHeader" class="timeline-header">
                    <div class="collaborator-header">
                        Colaborador(a)
                    </div>
                    <div class="approved-days-header">
                        <span class="approved-days-header__label">Dias aprovados</span>
                        <small>{{ approvedDaysYear }}</small>
                    </div>
                    <div class="timeline-header-content">
                        <div class="month-header">
                            <div
                                v-for="month in monthGroups"
                                :key="month.key"
                                class="month-header__item"
                                :style="{ gridColumn: `span ${month.length}` }"
                            >
                                {{ month.label }}
                            </div>
                        </div>
                        <div class="date-header text-primary bg-background">
                            <div
                                v-for="day in days"
                                :key="day.key"
                                :class="{ 'is-weekend bg-grey-darken-3 text-white': day.isWeekend }"
                                class="date-header__item "
                            >
                                <span>{{ day.day }}</span>
                                <small>{{ day.weekday.replace(".", "") }}</small>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="loading" class="timeline-skeleton">
                    <div
                        v-for="index in 8"
                        :key="index"
                        class="timeline-row timeline-row--skeleton"
                        :style="{
                            '--timeline-lanes': 1,
                            '--timeline-row-height': '44px',
                        }"
                    >
                        <div class="collaborator-cell">
                            <v-skeleton-loader type="text" width="100%" />
                        </div>
                        <div class="approved-days-cell">
                            <v-skeleton-loader type="text" width="32" />
                        </div>
                        <div class="timeline-days">
                            <v-skeleton-loader class="timeline-row-skeleton" type="image" />
                        </div>
                    </div>
                </div>

                <div v-else-if="error && !rows.length" class="timeline-state timeline-state--error">
                    Não foi possível carregar as ausências.
                </div>

                <div v-else-if="!rows.length" class="timeline-state">
                    Não foram encontrados colaboradores para os filtros selecionados.
                </div>

                <template v-else>
                    <div
                        v-if="virtualTopHeight"
                        aria-hidden="true"
                        class="timeline-virtual-spacer"
                        :style="{ height: `${virtualTopHeight}px` }"
                    ></div>

                    <div
                        v-for="row in virtualRows"
                        :key="row.user.id"
                        class="timeline-row"
                        :style="{
                            '--timeline-lanes': row.lanes,
                            '--timeline-row-height': `${row.height}px`,
                        }"
                    >
                        <div class="collaborator-cell">
                            <span class="collaborator-cell__code">{{ row.user.worker_code ?? "—" }}</span>
                            <span>{{ row.user.name ?? "Nome indisponível" }}</span>
                        </div>
                        <div
                            :class="approvedDaysColor(row.user)"
                            class="approved-days-cell"
                        >
                            {{ approvedDaysLabel(row.user) }}
                        </div>

                        <div class="timeline-days">
                            <div
                                v-for="day in days"
                                :key="day.key"
                                :class="{ 'is-weekend bg-grey-darken-3': day.isWeekend }"
                                class="timeline-day"
                            ></div>

                            <div class="timeline-bars">
                                <v-tooltip
                                    v-for="bar in row.bars"
                                    :key="bar.request.id"
                                    location="top"
                                >
                                    <template #activator="{ props: tooltipProps }">
                                        <div
                                            v-bind="tooltipProps"
                                            :class="[
                                                bar.status === 'processed'
                                                ? 'bg-success'
                                                : (
                                                    bar.status === 'approved'
                                                    ? 'bg-info'
                                                    : 'bg-warning'
                                                ),
                                                'absence-bar',
                                            ]"
                                            :style="{
                                                gridColumn: `${bar.start + 1} / span ${bar.length}`,
                                                gridRow: bar.lane + 1,
                                            }"
                                        >
                                            <span v-if="bar.length >= 3">{{ bar.label }}</span>
                                        </div>
                                    </template>
                                    <div>
                                        <strong>{{ row.user.name ?? "Colaborador" }}</strong><br>
                                        {{ statusLabel(bar.status) }} · {{ formatDate(bar.startDate) }} a {{ formatDate(bar.endDate) }}
                                    </div>
                                </v-tooltip>
                            </div>
                        </div>
                    </div>

                    <div
                        v-if="virtualBottomHeight"
                        aria-hidden="true"
                        class="timeline-virtual-spacer"
                        :style="{ height: `${virtualBottomHeight}px` }"
                    ></div>

                </template>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

const props = defineProps({
    days: {
        type: Array,
        default: () => [],
    },
    collaborators: {
        type: Array,
        default: () => [],
    },
    requests: {
        type: Array,
        default: () => [],
    },
    loading: {
        type: Boolean,
        default: false,
    },
    error: {
        type: Boolean,
        default: false,
    },
    approvedDaysYear: {
        type: Number,
        required: true,
    },
});

const scrollContainer = ref(null);
const timelineHeader = ref(null);
const scrollTop = ref(0);
const viewportHeight = ref(600);
const timelineHeaderHeight = ref(68);
const virtualRowBuffer = 10;
const defaultRowHeight = 44;

const monthGroups = computed(() => {
    const groups = [];

    props.days.forEach((day) => {
        const key = `${day.date.getFullYear()}-${day.date.getMonth()}`;
        const lastGroup = groups.at(-1);

        if (lastGroup?.key === key) {
            lastGroup.length += 1;
            return;
        }

        groups.push({
            key,
            label: `${day.month.charAt(0).toUpperCase()}${day.month.slice(1)} ${day.date.getFullYear()}`,
            length: 1,
        });
    });

    return groups;
});

const requestsByUserId = computed(() => {
    const result = new Map();

    props.requests.forEach((request) => {
        const userId = request?.user_id ?? request?.user?.id;

        if (!userId) {
            return;
        }

        const userRequests = result.get(userId) ?? [];

        userRequests.push(request);
        result.set(userId, userRequests);
    });

    return result;
});

const rows = computed(() => {
    if (!props.days.length) {
        return [];
    }

    const firstDay = props.days[0].key;
    const lastDay = props.days.at(-1).key;

    return props.collaborators.map((user) => {
        const bars = (requestsByUserId.value.get(user.id) ?? [])
            .map((request) => createBar(request, firstDay, lastDay))
            .filter(Boolean)
            .sort((firstBar, secondBar) => firstBar.start - secondBar.start || firstBar.end - secondBar.end);
        const laneEnds = [];

        bars.forEach((bar) => {
            const availableLane = laneEnds.findIndex((laneEnd) => laneEnd < bar.start);
            const lane = availableLane === -1 ? laneEnds.length : availableLane;

            bar.lane = lane;
            laneEnds[lane] = bar.end;
        });

        const lanes = Math.max(laneEnds.length, 1);

        return {
            user,
            bars,
            lanes,
            height: Math.max(44, lanes * 30 + 12),
        };
    });
});

const rowMetrics = computed(() => {
    let top = 0;
    const metrics = rows.value.map((row) => {
        const height = row.height;
        const metric = {
            row,
            top,
            bottom: top + height,
        };

        top += height;

        return metric;
    });

    return {
        metrics,
        totalHeight: top,
    };
});

const virtualRange = computed(() => {
    const metrics = rowMetrics.value.metrics;

    if (!metrics.length) {
        return {
            start: 0,
            end: 0,
        };
    }

    const visibleTop = Math.max(
        0,
        scrollTop.value - timelineHeaderHeight.value - virtualRowBuffer * defaultRowHeight,
    );
    const visibleBottom = scrollTop.value
        - timelineHeaderHeight.value
        + viewportHeight.value
        + virtualRowBuffer * defaultRowHeight;
    let start = metrics.findIndex((metric) => metric.bottom > visibleTop);

    if (start === -1) {
        start = metrics.length - 1;
    }

    let end = start;

    while (end < metrics.length && metrics[end].top < visibleBottom) {
        end += 1;
    }

    return {
        start,
        end: Math.max(end, start + 1),
    };
});

const virtualRows = computed(() => {
    const { start, end } = virtualRange.value;

    return rowMetrics.value.metrics
        .slice(start, end)
        .map((metric) => metric.row);
});

const virtualTopHeight = computed(() => {
    const firstMetric = rowMetrics.value.metrics[virtualRange.value.start];

    return firstMetric?.top ?? 0;
});

const virtualBottomHeight = computed(() => {
    const { end } = virtualRange.value;
    const nextMetric = rowMetrics.value.metrics[end];

    return nextMetric
        ? rowMetrics.value.totalHeight - nextMetric.top
        : 0;
});

function createBar(request, firstDay, lastDay) {
    const startDate = normaliseDate(request?.start_date);
    const endDate = normaliseDate(request?.end_date);

    if (!startDate || !endDate || startDate > lastDay || endDate < firstDay) {
        return null;
    }

    const clippedStart = startDate < firstDay ? firstDay : startDate;
    const clippedEnd = endDate > lastDay ? lastDay : endDate;
    const start = differenceInDays(firstDay, clippedStart);
    const end = differenceInDays(firstDay, clippedEnd);
    const status = request?.status?.status ?? request?.status ?? "pending";

    return {
        request,
        status: ["processed", "approved", "pending"].includes(status) ? status : "pending",
        start,
        end,
        length: end - start + 1,
        startDate,
        endDate,
        label: statusLabel(status),
        lane: 0,
    };
}

function normaliseDate(value) {
    return typeof value === "string" ? value.slice(0, 10) : null;
}

function differenceInDays(startDate, endDate) {
    return Math.round((dateToUtc(endDate) - dateToUtc(startDate)) / 86400000);
}

function dateToUtc(date) {
    const [year, month, day] = date.split("-").map(Number);

    return Date.UTC(year, month - 1, day);
}

function statusLabel(status) {
    const labels = {
        processed: "Processado",
        approved: "Aprovado por processar",
        pending: "Pendente de aprovação",
    };

    return labels[status] ?? "Pendente de aprovação";
}

function formatDate(date) {
    if (!date) {
        return "Data indisponível";
    }

    const [year, month, day] = date.split("-");

    return `${day}/${month}/${year}`;
}

function approvedDaysLabel(user) {
    const approvedDays = user?.absence_year_summary?.approved_days;

    return Number.isInteger(approvedDays) ? approvedDays : "—";
}

function approvedDaysColor(user) {
    const approvedDays = user?.absence_year_summary?.approved_days;

    if (!Number.isInteger(approvedDays)) {
        return "";
    }

    if (approvedDays <= 22) {
        return "text-success";
    }

    if (approvedDays <= 30) {
        return "text-warning";
    }

    return "text-error";
}

function handleScroll(event) {
    const scrollContainer = event.currentTarget;

    scrollTop.value = scrollContainer.scrollTop;
    viewportHeight.value = scrollContainer.clientHeight;
}

function updateViewport() {
    if (!scrollContainer.value) {
        return;
    }

    scrollTop.value = scrollContainer.value.scrollTop;
    viewportHeight.value = scrollContainer.value.clientHeight;
    timelineHeaderHeight.value = timelineHeader.value?.offsetHeight ?? 68;
}

onMounted(() => {
    updateViewport();
    window.addEventListener("resize", updateViewport);
});

onBeforeUnmount(() => {
    window.removeEventListener("resize", updateViewport);
});
</script>

<style scoped>
.timeline-shell {
    --collaborator-column-width: 280px;
    --approved-days-column-width: 132px;

    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    border-radius: 4px;
    overflow: hidden;
    background: rgb(var(--v-theme-surface));
}

.timeline-scroll {
    overflow: auto;
    max-height: calc(100vh - 280px);
}

.timeline-canvas {
    min-width: calc(
        var(--collaborator-column-width)
        + var(--approved-days-column-width)
        + (var(--timeline-columns) * 42px)
    );
}

.timeline-header,
.timeline-row {
    display: grid;
    grid-template-columns:
        var(--collaborator-column-width)
        var(--approved-days-column-width)
        minmax(0, 1fr);
}

.timeline-row--skeleton {
    overflow: hidden;
}

.timeline-virtual-spacer {
    grid-column: 1 / -1;
}

.timeline-header {
    position: sticky;
    top: 0;
    z-index: 4;
}

.collaborator-header,
.collaborator-cell {
    position: sticky;
    left: 0;
    z-index: 3;
    border-right: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.approved-days-header,
.approved-days-cell {
    position: sticky;
    left: var(--collaborator-column-width);
    z-index: 3;
    border-right: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    background: rgb(var(--v-theme-surface));
}

.collaborator-header {
    display: flex;
    align-items: center;
    padding: 0 16px;
    min-height: 68px;
    color: rgb(var(--v-theme-primary));
    font-size: 0.8rem;
    font-weight: 700;
    background: rgb(var(--v-theme-surface));
}

.approved-days-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 68px;
    padding: 0 8px;
    /* color: rgb(var(--v-theme-on-primary)); */
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1.2;
    text-align: center;
    /* background: rgb(var(--v-theme-primary)); */
}

.approved-days-header small {
    font-size: 0.7rem;
}

.timeline-header-content {
    min-width: 0;
    background: rgb(var(--v-theme-primary));
    color: rgb(var(--v-theme-on-primary));
}

.month-header,
.date-header,
.timeline-days,
.timeline-bars {
    display: grid;
    grid-template-columns: repeat(var(--timeline-columns), 42px);
}

.month-header {
    min-height: 32px;
}

.month-header__item {
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    padding: 4px 16px;
    border-right: 1px solid rgba(var(--v-theme-on-primary), 0.2);
    font-size: 0.78rem;
    font-weight: 700;
    line-height: 1.2;
    text-transform: capitalize;
}

.month-header__item:first-child {
    justify-content: flex-start;
}

.date-header {
    min-height: 36px;
    /* color: rgb(var(--v-theme-on-surface));
    background: rgb(var(--v-theme-surface-variant)); */
}

.date-header__item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-right: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    font-size: 0.75rem;
    line-height: 1.1;
}

.date-header__item small {
    font-size: 0.62rem;
    text-transform: capitalize;
}

.is-weekend {
    /* background-color: rgba(var(--v-theme-on-surface), 0.07); */
}

.timeline-row {
    min-height: var(--timeline-row-height);
    border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.collaborator-cell {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: var(--timeline-row-height);
    padding: 0 16px;
    background: rgb(var(--v-theme-surface));
    font-size: 0.85rem;
    font-weight: 600;
}

.approved-days-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: var(--timeline-row-height);
    padding: 0 8px;
    font-size: 0.9rem;
    font-weight: 700;
}

.collaborator-cell__code {
    min-width: 38px;
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
    font-size: 0.75rem;
    font-weight: 700;
}

.timeline-days {
    position: relative;
    min-height: var(--timeline-row-height);
}

.timeline-day {
    border-right: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.timeline-row-skeleton {
    grid-column: 1 / -1;
    width: 100%;
    height: var(--timeline-row-height);
}

.timeline-bars {
    position: absolute;
    inset: 0;
    grid-template-rows: repeat(var(--timeline-lanes), 24px);
    align-content: start;
    row-gap: 6px;
    padding-top: 6px;
    pointer-events: none;
}

.absence-bar {
    min-width: 0;
    height: 24px;
    margin: 0 1px;
    padding: 0 5px;
    overflow: hidden;
    /* color: rgb(var(--v-theme-on-primary)); */
    font-size: 0.7rem;
    font-weight: 700;
    line-height: 24px;
    text-overflow: ellipsis;
    white-space: nowrap;
    border-radius: 2px;
    cursor: default;
    pointer-events: auto;
}

.absence-bar--processed {
    background-color: rgb(var(--v-theme-success));
}

.absence-bar--approved {
    background-color: #ed8a19;
}

.absence-bar--pending {
    color: rgba(0, 0, 0, 0.78);
    background-color: #f2c94c;
}

.timeline-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    min-height: 160px;
    padding: 24px;
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
    text-align: center;
}

.timeline-state--error {
    color: rgb(var(--v-theme-error));
}

@media (max-width: 600px) {
    .timeline-shell {
        --collaborator-column-width: 220px;
        --approved-days-column-width: 112px;
    }

    .timeline-header,
    .timeline-row {
        grid-template-columns:
            var(--collaborator-column-width)
            var(--approved-days-column-width)
            minmax(0, 1fr);
    }

    .collaborator-header,
    .collaborator-cell {
        padding-right: 10px;
        padding-left: 10px;
    }
}

@media (max-width: 480px) {
    .timeline-shell {
        --collaborator-column-width: 100px;
        --approved-days-column-width: 40px;
    }

    .collaborator-header,
    .collaborator-cell {
        padding-right: 6px;
        padding-left: 6px;
    }

    .collaborator-header {
        font-size: 0.7rem;
    }

    .collaborator-cell {
        gap: 4px;
        font-size: 0.72rem;
        flex-direction: column;
    }

    .collaborator-cell > span:last-child {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .collaborator-cell__code {
        min-width: 32px;
        font-size: 0.68rem;
    }

    .approved-days-header {
        padding: 0 2px;
        font-size: 0.65rem;
    }

    .approved-days-header__label {
        font-size: 0;
    }

    .approved-days-header__label::after {
        content: "Aprov.";
        font-size: 0.65rem;
    }

    .approved-days-header small {
        font-size: 0.65rem;
    }

    .approved-days-cell {
        padding: 0 2px;
        font-size: 0.8rem;
    }
}
</style>
